/**
 * ReportNetworkAnalysis.js v2 — Network analysis + IP Geolocation
 * Top IPs, ports, protocols + live geo lookup via ip-api.com
 */
import React, { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { getPortInfo, SEVERITY_COLORS } from "../reportUtils";

// ── IP Geolocation via ip-api.com (free, no key, CORS allowed) ───────────────

const geoCache = {};

async function lookupIP(ip) {
  if (geoCache[ip]) return geoCache[ip];
  if (!ip || ip === "0.0.0.0" || ip === "Unknown source" || ip.includes("Unknown")) return null;
  try {
    const res  = await fetch(`https://ip-api.com/json/${ip}?fields=status,country,countryCode,regionName,city,isp,org,as,proxy,hosting,lat,lon`);
    const data = await res.json();
    if (data.status === "success") { geoCache[ip] = data; return data; }
  } catch {}
  return null;
}

const FLAG = (cc) => cc
  ? String.fromCodePoint(...[...cc.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0)))
  : "🌐";

// ── Component ─────────────────────────────────────────────────────────────────

export default function ReportNetworkAnalysis({ stats, theme, CHART_COLORS: CC, SEVERITY_COLORS: SC }) {
  if (!stats) return null;

  const [geoData,    setGeoData]    = useState({});
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoLookedUp,setGeoLookedUp]= useState(false);

  const portData = stats.portDist.slice(0, 15).map(p => ({
    ...p, info: getPortInfo(p.name),
    color: { critical: SC.critical, high: SC.high, medium: SC.medium, low: SC.low }[getPortInfo(p.name).risk] || SC.info,
  }));

  // Attack-port alert
  const attackPorts   = stats.attackPortDist || [];
  const hasAttackPort = attackPorts.length > 0;

  // Geo lookup for top source IPs
  const runGeoLookup = useCallback(async () => {
    if (geoLookedUp || !stats.srcIPDist.length) return;
    setGeoLoading(true);
    setGeoLookedUp(true);
    const ips = stats.srcIPDist.slice(0, 10).map(d => d.name);
    const results = {};
    for (const ip of ips) {
      results[ip] = await lookupIP(ip);
      await new Promise(r => setTimeout(r, 80)); // respect rate limit
    }
    setGeoData(results);
    setGeoLoading(false);
  }, [stats.srcIPDist, geoLookedUp]);

  // Auto-run geo lookup if IPs exist
  useEffect(() => {
    if (stats.srcIPDist.length > 0 && !geoLookedUp) runGeoLookup();
  }, [stats.srcIPDist, runGeoLookup, geoLookedUp]);

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#00695C">Network Analysis</SectionTitle>

      {/* Attack Vector Alert */}
      {hasAttackPort && (
        <div style={{ background: `${SC.critical}0F`, border: `1px solid ${SC.critical}44`, borderRadius: 8, padding: "12px 18px", marginBottom: 16, fontFamily: "monospace", fontSize: 12 }}>
          <span style={{ color: SC.critical, fontWeight: 700 }}>⚠ ATTACK VECTOR CONFIRMED: </span>
          <span style={{ color: "#FFCDD2" }}>
            {attackPorts.slice(0, 3).map(p => {
              const info = getPortInfo(p.name);
              return `Port ${p.name} (${info.service}) — ${p.value.toLocaleString()} confirmed attack events`;
            }).join(" · ")}
          </span>
          <div style={{ color: "#EF9A9A", fontSize: 11, marginTop: 4 }}>
            These ports were targeted exclusively by confirmed malicious traffic — not benign flows
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Source IPs */}
        <div className="report-card chart-container" style={cardStyle(theme)}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12 }}>
            TOP SOURCE IPs (ATTACKERS)
          </div>
          {stats.srcIPDist.length === 0 ? (
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, padding: "20px 0", textAlign: "center" }}>
              No source IP column detected in dataset
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.srcIPDist.slice(0,10)} layout="vertical" margin={{ left: 130, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.cardBorder} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: theme.colors.textMuted }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: theme.colors.textPrimary }} width={130} />
                <Tooltip contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, fontSize: 12 }} formatter={(v) => [v.toLocaleString(), "Events"]} />
                <Bar dataKey="value" radius={[0,4,4,0]} fill={SC.critical} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Dest IPs */}
        <div className="report-card chart-container" style={cardStyle(theme)}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12 }}>
            TOP DESTINATION IPs (TARGETS)
          </div>
          {stats.dstIPDist.length === 0 ? (
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, padding: "20px 0", textAlign: "center" }}>
              No destination IP column detected in dataset
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.dstIPDist.slice(0,10)} layout="vertical" margin={{ left: 130, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.cardBorder} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: theme.colors.textMuted }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: theme.colors.textPrimary }} width={130} />
                <Tooltip contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, fontSize: 12 }} formatter={(v) => [v.toLocaleString(), "Events"]} />
                <Bar dataKey="value" radius={[0,4,4,0]} fill={SC.high} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* IP Geolocation Table */}
      <div className="report-card" style={{ ...cardStyle(theme), marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, fontWeight: 700 }}>
            🌍 IP GEOLOCATION — ATTACKER ORIGIN INTELLIGENCE
          </div>
          {!geoLookedUp && stats.srcIPDist.length > 0 && (
            <button onClick={runGeoLookup} className="no-print" style={{
              padding: "6px 14px", background: "rgba(0,105,92,0.1)",
              border: "1px solid rgba(0,105,92,0.4)", borderRadius: 6,
              color: "#00695C", fontFamily: "monospace", fontSize: 10,
              fontWeight: 700, letterSpacing: 1, cursor: "pointer"
            }}>🔍 LOOKUP GEOs</button>
          )}
          {geoLoading && <span style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>◌ Looking up IPs…</span>}
        </div>

        {stats.srcIPDist.length === 0 ? (
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, textAlign: "center", padding: "16px 0" }}>
            No source IP data available — dataset does not contain IP address columns
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
            <thead>
              <tr style={{ background: theme.colors.tableHeader }}>
                {["#","Flag","IP Address","Events","Country","City / Region","ISP / Organization","ASN","Type"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 10, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.srcIPDist.slice(0, 10).map((ip, i) => {
                const geo    = geoData[ip.name];
                const isVPN  = geo?.proxy;
                const isHost = geo?.hosting;
                const typeLabel = isVPN ? "VPN/Proxy" : isHost ? "Hosting/DC" : geo ? "Residential/ISP" : geoLookedUp ? "Unknown" : "—";
                const typeColor = isVPN ? SC.critical : isHost ? SC.high : geo ? "#1565C0" : theme.colors.textMuted;
                return (
                  <tr key={i} style={{ background: i%2===0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                    <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>{i+1}</td>
                    <td style={{ padding: "7px 10px", fontSize: 18 }}>{geo ? FLAG(geo.countryCode) : "🌐"}</td>
                    <td style={{ padding: "7px 10px", color: SC.critical, fontWeight: 700, fontFamily: "monospace" }}>{ip.name}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>{ip.value.toLocaleString()}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textPrimary }}>{geo?.country || (geoLookedUp ? "Unknown" : "—")}</td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>
                      {geo ? `${geo.city}, ${geo.regionName}` : "—"}
                    </td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textMuted, fontSize: 10 }}>
                      {geo?.isp || "—"}
                      {geo?.org && geo.org !== geo.isp ? <div style={{ color: theme.colors.textMuted, fontSize: 9 }}>{geo.org}</div> : null}
                    </td>
                    <td style={{ padding: "7px 10px", color: theme.colors.textMuted, fontSize: 9 }}>{geo?.as?.split(" ")[0] || "—"}</td>
                    <td style={{ padding: "7px 10px" }}>
                      <span style={{ padding: "2px 6px", background: `${typeColor}18`, border: `1px solid ${typeColor}44`, borderRadius: 4, color: typeColor, fontSize: 9, fontWeight: 700 }}>
                        {typeLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Geo summary */}
        {Object.keys(geoData).length > 0 && (() => {
          const countries = {};
          Object.values(geoData).filter(Boolean).forEach(g => {
            countries[g.country] = (countries[g.country] || 0) + 1;
          });
          const vpnCount  = Object.values(geoData).filter(g => g?.proxy).length;
          const hostCount = Object.values(geoData).filter(g => g?.hosting).length;
          return (
            <div style={{ marginTop: 12, padding: "10px 14px", background: theme.colors.tableHeader, borderRadius: 6, display: "flex", gap: 24, flexWrap: "wrap", fontFamily: "monospace", fontSize: 11 }}>
              <span style={{ color: theme.colors.textPrimary }}>Countries: <strong>{Object.keys(countries).length}</strong></span>
              <span style={{ color: theme.colors.textPrimary }}>
                {Object.entries(countries).sort((a,b)=>b[1]-a[1]).slice(0,4).map(([c,n]) => `${c} (${n})`).join(" · ")}
              </span>
              {vpnCount > 0  && <span style={{ color: SC.critical, fontWeight: 700 }}>⚠ {vpnCount} VPN/Proxy detected</span>}
              {hostCount > 0 && <span style={{ color: SC.high,     fontWeight: 700 }}>⚠ {hostCount} Hosting/DC (possibly botnet)</span>}
            </div>
          );
        })()}

        <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted }}>
          Geolocation data: ip-api.com (ISP/city level — street-level precision requires law enforcement subpoena to ISP)
        </div>
      </div>

      {/* Port Analysis */}
      <div className="report-card" style={{ ...cardStyle(theme), marginBottom: 16 }}>
        <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12 }}>
          TARGETED PORTS — SERVICE & APPLICATION CONTEXT
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ background: theme.colors.tableHeader }}>
              {["Port","Service","Application / Tool","Events","%","Risk","Attack Target?"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 10, letterSpacing: 1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {portData.map((p, i) => {
              const isAttackPort = attackPorts.some(ap => ap.name === p.name);
              return (
                <tr key={i} style={{ background: isAttackPort ? `${SC.critical}14` : i%2===0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                  <td style={{ padding: "7px 10px", color: p.color, fontWeight: 900 }}>{p.name}</td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700 }}>{p.info.service}</td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>{p.info.app}</td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textPrimary }}>{p.value.toLocaleString()}</td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>{((p.value/stats.total)*100).toFixed(1)}%</td>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ padding: "2px 6px", background: `${p.color}18`, border: `1px solid ${p.color}44`, borderRadius: 4, color: p.color, fontSize: 9, fontWeight: 700 }}>
                      {p.info.risk.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    {isAttackPort
                      ? <span style={{ padding: "2px 8px", background: `${SC.critical}18`, border: `1px solid ${SC.critical}44`, borderRadius: 4, color: SC.critical, fontSize: 9, fontWeight: 700 }}>✓ CONFIRMED</span>
                      : <span style={{ color: theme.colors.textMuted, fontSize: 10 }}>—</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Protocol Distribution */}
      {stats.protoDist.length > 0 && (
        <div className="report-card" style={cardStyle(theme)}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12 }}>PROTOCOL DISTRIBUTION</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {stats.protoDist.map((p, i) => (
              <div key={i} style={{ background: `${CC[i%CC.length]}18`, border: `1px solid ${CC[i%CC.length]}44`, borderRadius: 8, padding: "10px 16px", textAlign: "center", minWidth: 80 }}>
                <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: CC[i%CC.length] }}>{p.value.toLocaleString()}</div>
                <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = (theme) => ({
  background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20,
});
const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily:"monospace", fontSize:13, fontWeight:900, color, letterSpacing:2, textTransform:"uppercase", borderLeft:`4px solid ${color}`, paddingLeft:12, marginBottom:16 }}>{children}</div>
);
